/* eslint-disable react-refresh/only-export-components */
import type { LinkProps } from '@tanstack/react-router';
import type { HTMLAttributes } from 'react';

import { Link, Outlet } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import React, { createContext, use, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { ButtonProps } from '../../../components/button';

import { Button } from '../../../components/button';
import { useScreenSize } from '../../../hooks/use-screen-size';
import { cn } from '../../../lib/utils';
import { FixedUIIntersectionProvider } from '../hooks/use-fixed-ui-intersection';
import { useHeaderIntersection } from '../hooks/use-header-intersection';
import { StickyHeaderContext, useStickyHeaderContext } from '../hooks/use-sticky-header-context';

// ============================================================================
// Page Root - Main content layout wrapper with sticky header support
// ============================================================================

export type PageProps = HTMLAttributes<HTMLDivElement>;

export function PageRoot({ className, style, ...props }: PageProps) {
    const [stickyOffset, setStickyOffset] = useState(0);
    const [headerNode, setHeaderNode] = useState<HTMLDivElement | null>(null);

    const registerHeader = useCallback((node: HTMLDivElement | null) => {
        setHeaderNode(node);
        if (node) {
            setStickyOffset(node.offsetHeight);
        }
    }, []);

    useEffect(() => {
        if (!headerNode)
            return;

        const observer = new ResizeObserver(() => {
            setStickyOffset(headerNode.offsetHeight);
        });
        observer.observe(headerNode);

        return () => observer.disconnect();
    }, [headerNode]);

    const contextValue = useMemo(
        () => ({ stickyOffset, registerHeader }),
        [stickyOffset, registerHeader],
    );

    return (
        <StickyHeaderContext value={contextValue}>
            <FixedUIIntersectionProvider>
                <div
                    className={cn(
                        'flex w-full flex-col xl:max-w-[964px] mx-auto xl:py-2',
                        className,
                    )}
                    style={{
                        '--sticky-offset': `${stickyOffset}px`,
                        ...style,
                    } as React.CSSProperties}
                    {...props}
                />
            </FixedUIIntersectionProvider>
        </StickyHeaderContext>
    );
}

// ============================================================================
// Page Title
// ============================================================================

export type PageTitleProps = HTMLAttributes<HTMLHeadingElement>;

export function PageTitle({ className, ...props }: PageTitleProps) {
    return <h1 className={cn('text-xl font-bold truncate', className)} {...props} />;
}

// ============================================================================
// Page SubTitle
// ============================================================================

export type PageSubTitleProps = HTMLAttributes<HTMLHeadingElement>;

export function PageSubTitle({ className, ...props }: PageSubTitleProps) {
    return <h2 className={cn('text-sm font-semibold text-text-tertiary truncate', className)} {...props} />;
}

// ============================================================================
// Page Outlet
// ============================================================================

export type PageOutletProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
    children?: React.ReactNode;
};

export function PageOutlet({ className, children, ...props }: PageOutletProps) {
    return (
        <div className={cn('flex flex-col gap-4', className)} {...props}>
            {children ?? <Outlet />}
        </div>
    );
}

// ============================================================================
// Page Header - Sticky header wrapper
// ============================================================================

type PageHeaderContextValue = {
    /**
     * Padding style for components that need to reserve space for fixed UI elements.
     */
    paddingStyle?: React.CSSProperties;
    /**
     * Register a component as wanting to handle padding. Returns true if this component
     * should apply the padding (i.e., it's the first one registered).
     */
    registerForPadding: (id: string) => boolean;
    /**
     * Unregister a component from padding handling.
     */
    unregisterForPadding: (id: string) => void;
    /**
     * Get the ID of the component that should handle padding.
     */
    paddingOwnerId: string | null;
};

const PageHeaderContext = createContext<PageHeaderContextValue | null>(null);

/**
 * Hook to consume the PageHeader context.
 */
export function usePageHeaderContext(): PageHeaderContextValue {
    const context = use(PageHeaderContext);
    if (!context) {
        return {
            paddingStyle: undefined,
            registerForPadding: () => false,
            unregisterForPadding: () => {},
            paddingOwnerId: null,
        };
    }
    return context;
}

export type PageHeaderProps = HTMLAttributes<HTMLDivElement> & {
    ref?: React.Ref<HTMLDivElement>;
};

export function PageHeaderRoot({ className, ref, children, style, ...props }: PageHeaderProps) {
    const stickyContext = useStickyHeaderContext();
    const internalRef = useRef<HTMLDivElement>(null);
    const { isDesktop } = useScreenSize();

    // Use intersection detection to reserve space for fixed UI elements
    // Page header is always at top (sticky top-0), so use alwaysReserve for initial state
    const intersection = useHeaderIntersection(internalRef, { alwaysReserve: true });

    const combinedRef = useCallback(
        (node: HTMLDivElement | null) => {
            // Store in internal ref
            internalRef.current = node;

            // Register with context if available
            stickyContext?.registerHeader(node);

            // Forward the external ref
            if (typeof ref === 'function') {
                ref(node);
            }
            else if (ref) {
                ref.current = node;
            }
        },
        [stickyContext, ref],
    );

    // Calculate padding styles for the first child that needs to avoid fixed UI elements
    const paddingStyle = useMemo(
        () => (isDesktop
            ? undefined
            : {
                    paddingLeft: intersection.shouldReserveLeft
                        ? `${intersection.paddingLeft}px`
                        : undefined,
                    paddingRight: intersection.shouldReserveRight
                        ? `${intersection.paddingRight}px`
                        : undefined,
                }),
        [isDesktop, intersection.shouldReserveLeft, intersection.shouldReserveRight, intersection.paddingLeft, intersection.paddingRight],
    );

    // Track which component owns the padding (first one to register)
    const [paddingOwnerId, setPaddingOwnerId] = useState<string | null>(null);
    const registeredIdsRef = useRef<Set<string>>(new Set());

    const registerForPadding = useCallback((id: string): boolean => {
        registeredIdsRef.current.add(id);
        // First registered component becomes the owner
        if (registeredIdsRef.current.size === 1) {
            setPaddingOwnerId(id);
            return true;
        }
        return paddingOwnerId === id;
    }, [paddingOwnerId]);

    const unregisterForPadding = useCallback((id: string): void => {
        registeredIdsRef.current.delete(id);
        if (paddingOwnerId === id) {
            // Transfer ownership to next registered component, if any
            const remaining = Array.from(registeredIdsRef.current);
            setPaddingOwnerId(remaining.length > 0 ? remaining[0] : null);
        }
    }, [paddingOwnerId]);

    const contextValue = useMemo(
        () => ({
            paddingStyle,
            registerForPadding,
            unregisterForPadding,
            paddingOwnerId,
        }),
        [paddingStyle, registerForPadding, unregisterForPadding, paddingOwnerId],
    );

    return (
        <PageHeaderContext value={contextValue}>
            <div
                ref={combinedRef}
                className={cn('sticky top-0 z-20 flex flex-col pb-2 bg-background-primary xl:bg-background', className)}
                style={style}
                {...props}
            >
                {children}
            </div>
        </PageHeaderContext>
    );
}

// ============================================================================
// Page Header Bar - The main header row with back, title, and actions
// ============================================================================

export type PageHeaderBarProps = HTMLAttributes<HTMLDivElement>;

const PAGE_HEADER_BAR_ID = 'page-header-bar';

export function PageHeaderBar({ className, style, ...props }: PageHeaderBarProps) {
    const { paddingStyle, registerForPadding, unregisterForPadding, paddingOwnerId } = usePageHeaderContext();

    // Register on mount, unregister on unmount (useLayoutEffect to minimize flash)
    useLayoutEffect(() => {
        registerForPadding(PAGE_HEADER_BAR_ID);
        return () => unregisterForPadding(PAGE_HEADER_BAR_ID);
    }, [registerForPadding, unregisterForPadding]);

    // Only apply padding if this component owns it
    const shouldApplyPadding = paddingOwnerId === PAGE_HEADER_BAR_ID;

    return (
        <header
            className={cn(
                'flex items-center gap-4 pt-2.5 xl:pt-4 px-2 xl:px-0',
                className,
            )}
            style={{
                ...(shouldApplyPadding ? paddingStyle : undefined),
                ...style,
            }}
            {...props}
        />
    );
}

// ============================================================================
// Page Header Back - Navigation back button
// ============================================================================

export type PageHeaderBackProps = {
    className?: string;
} & Omit<LinkProps, 'children'>;

export function PageHeaderBack({ className, ...props }: PageHeaderBackProps) {
    return (
        <Button variant="ghost" size="icon" className={cn('shrink-0', className)} asChild>
            <Link {...props}>
                <ArrowLeft className="h-5 w-5" />
            </Link>
        </Button>
    );
}

// ============================================================================
// Page Header Title
// ============================================================================

export type PageHeaderTitleProps = HTMLAttributes<HTMLHeadingElement>;

export function PageHeaderTitle({ className, ...props }: PageHeaderTitleProps) {
    return (
        <h1
            className={cn('flex-1 text-xl leading-10 font-semibold truncate first:ps-4', className)}
            {...props}
        />
    );
}

// ============================================================================
// Page Header Actions - Container for action buttons
// ============================================================================

export type PageHeaderActionsProps = HTMLAttributes<HTMLDivElement>;

export function PageHeaderActions({ className, ...props }: PageHeaderActionsProps) {
    return (
        <div
            className={cn('flex items-center gap-1 shrink-0', className)}
            {...props}
        />
    );
}

// ============================================================================
// Page Header Action - Individual action button
// ============================================================================

export type PageHeaderActionProps = {
    icon: React.ReactNode;
} & ButtonProps;

export function PageHeaderAction({ icon, className, variant, size, outline, ...props }: PageHeaderActionProps) {
    return (
        <Button variant={variant ?? 'ghost'} size={size ?? 'icon'} outline={outline} className={cn('', className)} {...props}>
            {icon}
        </Button>
    );
}

// ============================================================================
// Page Header ActionLink - Action button as a link
// ============================================================================

export type PageHeaderActionLinkProps = {
    icon: React.ReactNode;
    className?: string;
} & Omit<LinkProps, 'children'>;

export function PageHeaderActionLink({ icon, className, ...props }: PageHeaderActionLinkProps) {
    return (
        <Button variant="ghost" size="icon" className={cn('', className)} asChild>
            <Link {...props}>
                {icon}
            </Link>
        </Button>
    );
}

// ============================================================================
// Page Header Nav - Navigation tabs container
// ============================================================================

export type PageHeaderNavProps = HTMLAttributes<HTMLUListElement>;

const PAGE_HEADER_NAV_ID = 'page-header-nav';

export function PageHeaderNav({ className, style, ...props }: PageHeaderNavProps) {
    const { paddingStyle, registerForPadding, unregisterForPadding, paddingOwnerId } = usePageHeaderContext();

    // Register on mount, unregister on unmount (useLayoutEffect to minimize flash)
    useLayoutEffect(() => {
        registerForPadding(PAGE_HEADER_NAV_ID);
        return () => unregisterForPadding(PAGE_HEADER_NAV_ID);
    }, [registerForPadding, unregisterForPadding]);

    // Only apply padding if this component owns it
    const shouldApplyPadding = paddingOwnerId === PAGE_HEADER_NAV_ID;

    return (
        <nav
            className="w-full overflow-hidden px-2 pt-2.5 transition-[padding] duration-200 xl:px-0"
            style={shouldApplyPadding ? paddingStyle : undefined}
        >
            <ul
                className={cn(
                    'flex w-full items-center overflow-x-auto overflow-y-hidden rounded-2xl border border-border-secondary bg-background-primary',
                    className,
                )}
                style={style}
                {...props}
            />
        </nav>
    );
}

// ============================================================================
// Page Header NavLink - Individual navigation link
// ============================================================================

export type PageHeaderNavLinkProps = {
    className?: string;
    children: React.ReactNode;
    exact?: boolean;
} & Omit<LinkProps, 'children'>;

export function PageHeaderNavLink({ className, ...props }: PageHeaderNavLinkProps) {
    return (
        <li className="flex-1 min-w-fit">
            <Button variant="ghost" className="w-full rounded-2xl" asChild>
                <Link
                    className={cn(
                        'relative block px-6 py-2 text-sm font-medium text-text-tertiary transition-colors border border-transparent hover:text-text-primary',
                        className,
                    )}
                    activeProps={{
                        className: cn('!text-primary !border-border hover:!bg-background-primary hover:!text-brand-primary'),
                    }}
                    {...props}
                />
            </Button>
        </li>
    );
}

// ============================================================================
// Compound Component Exports
// ============================================================================

const PageHeader = Object.assign(PageHeaderRoot, {
    Bar: PageHeaderBar,
    Back: PageHeaderBack,
    Title: PageHeaderTitle,
    Actions: PageHeaderActions,
    Action: PageHeaderAction,
    ActionLink: PageHeaderActionLink,
    Nav: PageHeaderNav,
    NavLink: PageHeaderNavLink,
});

export const Page = Object.assign(PageRoot, {
    Header: PageHeader,
    Title: PageTitle,
    SubTitle: PageSubTitle,
    Outlet: PageOutlet,
});
